import { createRoute, useNavigate, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ChevronLeft,
  Package,
  Gem,
  Gamepad2,
  Bike,
  Music,
  BookOpen,
  Shirt,
  Palette,
  Trophy,
  Coins,
  Stamp,
  Wrench,
  Wine,
  Dices,
  Flower2,
  type LucideIcon,
} from 'lucide-react';
import { customVaultDefinitionSchema } from '@siliconvault/shared';
import type { CustomFieldDef } from '@siliconvault/shared';
import { Input, Textarea } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { VaultFieldEditor } from '../components/vault/VaultFieldEditor';
import { useVault } from '../context/VaultContext';
import { useAppMode } from '../context/AppModeContext';
import { useTranslation } from '../i18n';
import { Shell } from '../components/layout/Shell';
import { Route as rootRoute } from './__root';
import styles from './vaults.custom.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vaults/custom',
  component: CreateCustomVaultPage,
});

const ICON_OPTIONS: { name: string; icon: LucideIcon }[] = [
  { name: 'Package', icon: Package },
  { name: 'Gem', icon: Gem },
  { name: 'Gamepad2', icon: Gamepad2 },
  { name: 'Bike', icon: Bike },
  { name: 'Music', icon: Music },
  { name: 'BookOpen', icon: BookOpen },
  { name: 'Shirt', icon: Shirt },
  { name: 'Palette', icon: Palette },
  { name: 'Trophy', icon: Trophy },
  { name: 'Coins', icon: Coins },
  { name: 'Stamp', icon: Stamp },
  { name: 'Wrench', icon: Wrench },
  { name: 'Wine', icon: Wine },
  { name: 'Dices', icon: Dices },
  { name: 'Flower2', icon: Flower2 },
];

function CreateCustomVaultPage() {
  const { createCustomVault, vaults } = useVault();
  const { mode, aiTier } = useAppMode();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('Package');
  const [platformLabel, setPlatformLabel] = useState(() => t('fieldEditor.defaultPlatformLabel'));
  const [fields, setFields] = useState<CustomFieldDef[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isVaultLimited = mode === 'web' && aiTier === 'free' && vaults.length >= 1;

  const handleSubmit = async () => {
    setError('');
    const definition = {
      displayName,
      description,
      icon,
      platformLabel: platformLabel || 'Brand',
      fields,
    };

    const validation = customVaultDefinitionSchema.safeParse(definition);
    if (!validation.success) {
      setError(validation.error.errors.map((e) => e.message).join('. '));
      return;
    }

    setSubmitting(true);
    try {
      await createCustomVault(displayName, validation.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('vaults.custom.createFailed'));
      return;
    } finally {
      setSubmitting(false);
    }
    navigate({ to: '/dashboard' });
  };

  return (
    <Shell>
      <div className={styles.page}>
        <div className={styles.header}>
          <Link to="/vaults" className={styles.backLink}>
            <ChevronLeft size={16} />
            {t('vaults.custom.backToVaults')}
          </Link>
          <h1 className={styles.title}>{t('vaults.custom.title')}</h1>
          <p className={styles.subtitle}>{t('vaults.custom.subtitle')}</p>
        </div>

        {/* Basic Info */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('vaults.custom.basicInfo')}</h2>
          <Input
            label={t('vaults.custom.vaultName')}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t('vaults.custom.vaultNamePlaceholder')}
          />
          <div style={{ height: 12 }} />
          <Textarea
            label={t('vaults.custom.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('vaults.custom.descriptionPlaceholder')}
            rows={3}
          />
          <div style={{ height: 12 }} />
          <div className={styles.row2}>
            <Input
              label={t('vaults.custom.platformLabel')}
              value={platformLabel}
              onChange={(e) => setPlatformLabel(e.target.value)}
              placeholder={t('vaults.custom.platformLabelPlaceholder')}
            />
            <div>
              <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
                {t('vaults.custom.icon')}
              </label>
              <div className={styles.iconGrid}>
                {ICON_OPTIONS.map(({ name, icon: IconComp }) => (
                  <button
                    key={name}
                    type="button"
                    className={`${styles.iconBtn}${icon === name ? ` ${styles.iconBtnActive}` : ''}`}
                    onClick={() => setIcon(name)}
                    title={name}
                  >
                    <IconComp size={20} strokeWidth={1.5} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <VaultFieldEditor
          fields={fields}
          onFieldsChange={setFields}
          platformLabel={platformLabel}
        />

        {error && <p className={styles.errorMsg}>{error}</p>}

        <div className={styles.footer}>
          <Button variant="secondary" onClick={() => navigate({ to: '/vaults' })}>
            {t('vaults.cancel')}
          </Button>
          {isVaultLimited ? (
            <Button variant="primary" onClick={() => navigate({ to: '/upgrade' })}>
              {t('vaults.custom.upgradeToCreate')}
            </Button>
          ) : (
            <Button
              variant="primary"
              loading={submitting}
              disabled={submitting || !displayName.trim() || fields.length === 0}
              onClick={handleSubmit}
            >
              {t('vaults.custom.createVault')}
            </Button>
          )}
        </div>
      </div>
    </Shell>
  );
}
